import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface ApplyChangesToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

export const ApplyChangesToolCall: React.FC<ApplyChangesToolCallProps> = ({
  result,
  status,
}) => {
  const queryClient = useQueryClient();
  const hasInvalidatedRef = useRef(false);

  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Check if the result indicates success
  let isSuccess = false;
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
    } catch (e) {
      isSuccess = !result.toLowerCase().includes('error');
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  // Invalidate all React Query caches when the tool completes successfully
  useEffect(() => {
    if (isSuccess && !hasInvalidatedRef.current) {
      hasInvalidatedRef.current = true;
      queryClient.invalidateQueries();
      console.log('[ApplyChangesToolCall] Invalidated all React Query caches after successful apply');
    }
  }, [isSuccess, queryClient]);

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning
          ? "bg-primary/10 border-b border-primary/20"
          : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Applying changes to develop mode
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Applying changes to develop mode
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Failed to apply changes
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Applying changes to develop mode
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Changes applied successfully
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
